import React, { useEffect, useState } from 'react'

import { isContainer } from '../types/dashboard'

import Button from '~/components/Common/Button'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/Common/PopoverDialog'
import Section from '~/components/Common/Section'

import { createPortal } from 'react-dom'
import { HiCog, HiLockClosed, HiRefresh } from 'react-icons/hi'
import { PanelGroup } from 'react-resizable-panels'

import { useDashboardContext } from '../context/DashboardContext/useContext'
import { useSwapy } from '../hooks/useSwapy'
import { dfsInspectIds } from '../utils/elementTree'
import { saveConfiguration } from '../utils/persistence'
import { DashboardElementRenderer } from './DashboardElementRenderer'
import { DashboardErrorBoundary } from './DashboardErrorBoundary'

export const ConfigurableDashboard: React.FC = () => {
  // ===== Context =====
  const { isConfigurable, setIsConfigurable, elements, resetDashboard, resetKey } = useDashboardContext()
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
  const [isResetPopoverOpen, setIsResetPopoverOpen] = useState(false)

  // ===== Effects =====
  useEffect(() => {
    setPortalContainer(document.getElementById('header')?.children[0] as HTMLElement)
  }, [])

  // ===== Drag and Drop =====
  const containerRef = useSwapy()

  const handleConfigurableChange = () => {
    setIsConfigurable((prev) => {
      saveConfiguration({ isConfigurable: !prev }, 'configurableDashboard')
      return !prev
    })
  }

  const handleReset = () => {
    resetDashboard()
    setIsResetPopoverOpen(false)
  }

  const baseElement = elements[0]
  if (!isContainer(baseElement)) throw new Error('Base element not found')

  dfsInspectIds(baseElement)

  return (
    <Section>
      {portalContainer &&
        createPortal(
          <div className="absolute right-0 top-0 z-10 flex gap-2">
            <Popover open={isResetPopoverOpen} onOpenChange={setIsResetPopoverOpen}>
              <PopoverTrigger asChild>
                <Button bordered>
                  <HiRefresh className="m-0.5 h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4">
                <div className="flex flex-col gap-3">
                  <p>Are you sure you want to reset the dashboard?</p>
                  <div className="flex justify-end gap-2">
                    <Button onClick={() => setIsResetPopoverOpen(false)} bordered>
                      Cancel
                    </Button>
                    <Button onClick={handleReset} variant="primary">
                      Reset
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button onClick={handleConfigurableChange} bordered>
              {isConfigurable ? (
                <>
                  <HiLockClosed className="mr-2" />
                  Lock
                </>
              ) : (
                <>
                  <HiCog className="mr-2" />
                  Configure
                </>
              )}
            </Button>
          </div>,
          portalContainer
        )}

      <div ref={containerRef} className="flex flex-col">
        <DashboardErrorBoundary resetKey={resetKey} onReset={resetDashboard}>
          <PanelGroup key={resetKey} direction={baseElement.direction} className="relative !overflow-visible">
            <DashboardElementRenderer element={baseElement} index={0} parentElement={null} />
          </PanelGroup>
        </DashboardErrorBoundary>
      </div>
    </Section>
  )
}
